import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import Form from '@lib/wrappers/Form';
import NamesOnDocumentInputs from './pageIncludes/common/names/NamesOnDocumentInputs';
import OtherDocumentsInputs from './pageIncludes/citizenshipImmigrationStatus/OtherDocumentsInputs';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import InstructionalText from '../notifications/InstructionalText';
import Tooltip from '../../common/tooltip/Tooltip';

// ActionCreators / Selectors

import { changeData, clearFormSubmission, fetchCountries } from '@actions/ssap/ssapActionCreators';
import { selectDacaEffective } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { omit, values } from '@utils/miscellaneous/objectHelpers';
import { isDateAfter } from '@utils/dates/date.utils';

// Constants

import { configs } from '@constants/configs/configs';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { ELIGIBILITY_DOCUMENTS } from '@constants/ssap/hhmTypes/documentsOfEligibility';
import { getAllDocumentsList } from '@constants/ssap/hhmTypes/otherImmigrationDocuments';
import config from '../../../../config/app.json';
import { ConditionalRendering } from '@components/common/conditional-rendering';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Citizenship and Immigrant status' Page
 *
 */
const Page2_09 = props => {

    const {
        common: {
            environment: {
                exchangeName,
                isState,
                stateCode,
                localDates: {
                    serverDate
                },
                links: {
                    PHONE
                }
            }
        },
        countries,
        data,
        form,
        formName,
        militaryActiveDutyReturnQuestionEnabled,
        pageName
    } = props;


    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const LEARN_MORE_LINK = config.globalConfig.externalUrls.immigrationStatusLink;

    const {
        citizenshipImmigrationStatus : {
            citizenshipStatusIndicator,
            citizenshipDocument: {
                alienNumber,
                cardNumber,
                sevisId,
                documentExpirationDate,
                foreignPassportCountryOfIssuance,
                foreignPassportOrDocumentNumber,
                i94Number,
                nameOnDocument:
                    {
                        middleName,
                        lastName,
                        firstName,
                        suffix
                    },
                nameSameOnDocumentIndicator,
                visaNumber
            },
            dateOfEntry,
            hasI864Sponsor,
            lawfulPresenceIndicator,
            otherImmigrationDocumentType,
            hasRefugeeStatus,
            naturalizedCitizenshipIndicator,
            livedIntheUSSince1996Indicator,
            eligibleImmigrationDocumentSelected,
            unchangedImmigrationStatusLastFiveYears
        },
        dateOfBirth,
        name,
        gender
    } = currentHhm;

    const citizenshipDocumentFields = {
        alienNumber,
        cardNumber,
        sevisId,
        documentExpirationDate,
        foreignPassportCountryOfIssuance,
        foreignPassportOrDocumentNumber,
        i94Number,
        middleName,
        lastName,
        firstName,
        suffix,
        visaNumber
    }; // (not all citizenshipDocument properties are necessary, hence this new object)

    const dacaEffective = useSelector(selectDacaEffective);

    const currentData = data
        .taxHousehold
        .householdMember[member];

    const documents = data
        .taxHousehold
        .householdMember[member]
        .citizenshipImmigrationStatus
        .eligibleImmigrationDocumentType[0];

    const shouldRenderSameOnDocument = values(omit(documents, 'noneOfThese')).some(v => v);

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    // Preparing for Local State Eligibility Documents to handle if one of them is checked or not

    const prepareDocumentList = () => {
        const documents = data
            .taxHousehold
            .householdMember[member]
            .citizenshipImmigrationStatus
            .eligibleImmigrationDocumentType[0];

        return ELIGIBILITY_DOCUMENTS.map((doc, i) => ({
            documentStatus: documents && documents[doc.name] || false,
            documentName: doc.name,
            documentText: doc.text,
            documentDetails: doc.details.reduce((acc, d, j) => {
                // Check if field is applicable for current state
                if (typeof d.isApplicableforState === 'function' && !d.isApplicableforState({ isState })) {
                    return acc;
                }

                const newDetails = Object.assign({}, d);

                if(d.name === 'foreignPassportCountryOfIssuance') {
                    newDetails.options = DROPDOWN_OPTIONS.COUNTRIES(countries);
                }

                const originallyRequired = ELIGIBILITY_DOCUMENTS[i].details[j].isInputRequired;

                if(documents.OtherDocumentTypeIndicator
                    && d.name === 'documentExpirationDate'
                ) {
                    newDetails.isInputRequired = foreignPassportOrDocumentNumber !== null && foreignPassportOrDocumentNumber !== '';
                    newDetails.isToggleReRegistration = foreignPassportOrDocumentNumber !== null && foreignPassportOrDocumentNumber !== '';
                } else if(originallyRequired) {
                    newDetails.isInputRequired = Object.values(citizenshipDocumentFields).some(field => field !== null && field !== '');
                    newDetails.isToggleReRegistration = Object.values(citizenshipDocumentFields).some(field => field !== null && field !== '');
                }

                acc.push(newDetails);
                return acc;
            }, [])
        }));
    };

    // Local State of Functional Component

    const [ localState, setLocalState ] = useState({
        documents: prepareDocumentList(),
        citizenshipCertificate: eligibleImmigrationDocumentSelected === 'CitizenshipCertificate',
        naturalizationCertificate: eligibleImmigrationDocumentSelected === 'NaturalizationCertificate'
    });

    // Here we make API call on mounting Component in DOM-structure to get countries list

    useEffect(() => {
        props.countries.length === 0 && props.actions.fetchCountries(`${configs.endpoints.COUNTRIES_ENDPOINT}`);
    }, []);

    const cleanUpCertificateRelatedData = data => {
        return data
            .taxHousehold
            .householdMember[member]
            .citizenshipImmigrationStatus
            .citizenshipDocument = cloneObject(EMPTY_OBJECTS.CITIZENSHIP_DOCUMENT);
    };

    useEffect(() => {
        if(localState.citizenshipCertificate) {
            const newState = { ...data };
            const currentValue = newState
                .taxHousehold
                .householdMember[member]
                .citizenshipImmigrationStatus
                .eligibleImmigrationDocumentSelected;

            newState
                .taxHousehold
                .householdMember[member]
                .citizenshipImmigrationStatus
                .eligibleImmigrationDocumentSelected = 'CitizenshipCertificate';
            if(currentValue !== 'CitizenshipCertificate') {
                cleanUpCertificateRelatedData(newState);
            }
            props.actions.changeData(newState);
        }
    }, [localState.citizenshipCertificate]);

    useEffect(() => {
        if(localState.naturalizationCertificate) {
            const newState = { ...data };
            const currentValue = newState
                .taxHousehold
                .householdMember[member]
                .citizenshipImmigrationStatus
                .eligibleImmigrationDocumentSelected;

            newState
                .taxHousehold
                .householdMember[member]
                .citizenshipImmigrationStatus
                .eligibleImmigrationDocumentSelected = 'NaturalizationCertificate';
            if(currentValue !== 'NaturalizationCertificate') {
                cleanUpCertificateRelatedData(newState);
            }
            props.actions.changeData(newState);
        }
    }, [localState.naturalizationCertificate]);

    useEffect(() => {
        !naturalizedCitizenshipIndicator && props.actions.clearFormSubmission(formName);
    }, [naturalizedCitizenshipIndicator]);

    // Updating some properties in JSON
    const onFieldChange = (...args) => {

        const { name, newData, value } = parseFieldChangeArgs(args);

        const {
            citizenshipStatusIndicator,
            livedIntheUSSince1996Indicator,
            unchangedImmigrationStatusLastFiveYears
        } = newData.citizenshipImmigrationStatus;

        if(livedIntheUSSince1996Indicator) {
            newData.citizenshipImmigrationStatus = {
                ...newData.citizenshipImmigrationStatus,
                honorablyDischargedOrActiveDutyMilitaryMemberIndicator: null,
                militaryFamilyMemberIndicator: null,
                unchangedImmigrationStatusLastFiveYears: null
            };
            newData.returnedFromTourOfActiveDuty = null;
            newData.dateLastActiveTour = null;
        }

        if(unchangedImmigrationStatusLastFiveYears && militaryActiveDutyReturnQuestionEnabled) {
            newData.citizenshipImmigrationStatus = {
                ...newData.citizenshipImmigrationStatus,
                honorablyDischargedOrActiveDutyMilitaryMemberIndicator: null,
                militaryFamilyMemberIndicator: null
            };
            newData.returnedFromTourOfActiveDuty = null;
            newData.dateLastActiveTour = null;
        }

        if(citizenshipStatusIndicator) {
            newData.citizenshipImmigrationStatus = {
                ...cloneObject(EMPTY_OBJECTS.CITIZENSHIP_STATUS),
                citizenshipStatusIndicator: true
            };
            newData.returnedFromTourOfActiveDuty = null;
            newData.dateLastActiveTour = null;
        }

        if (getAllDocumentsList(isState).some(d => d.name === name)) {
            if(name === 'noneOfTheseOther') {
                if(value) {
                    newData.citizenshipImmigrationStatus.otherImmigrationDocumentType = {
                        ...EMPTY_OBJECTS.OTHER_IMMIGRATION_DOCUMENTS,
                        noneOfThese: value
                    };
                }
            } else {
                if (isState.MN) {
                    //Reset all flags to false for MN since only one selection allowed
                    newData.citizenshipImmigrationStatus.otherImmigrationDocumentType = {
                        ...EMPTY_OBJECTS.OTHER_IMMIGRATION_DOCUMENTS,
                        [name]: value,
                        noneOfThese: !value
                    };
                    if (name === 'lawfulPermanentResidentIndicator') {
                        newData.citizenshipImmigrationStatus.hasRefugeeStatus = false;
                    }
                } else {
                    newData.citizenshipImmigrationStatus.otherImmigrationDocumentType = {
                        ...newData.citizenshipImmigrationStatus.otherImmigrationDocumentType,
                        [name]: value,
                        noneOfThese: value ?
                            false :
                            Object
                                .values(omit(newData.citizenshipImmigrationStatus.otherImmigrationDocumentType, ['noneOfThese']))
                                .every(v => !v)
                    };
                }
            }
        }

        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChange_naturalized = newData => {

        setLocalState(state => ({
            ...state,
            citizenshipCertificate: false,
            naturalizationCertificate: false
        }));
        if(!newData.citizenshipImmigrationStatus.naturalizedCitizenshipIndicator) {
            newData.citizenshipImmigrationStatus = {
                ...cloneObject(EMPTY_OBJECTS.CITIZENSHIP_STATUS),
                citizenshipStatusIndicator: true,
                naturalizedCitizenshipIndicator: false,
                eligibleImmigrationDocumentSelected: null
            };
        }
        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);
    };

    // Updating checking certificate inputs

    const onFieldChange_certificate = (...args) => {
        const { name, newData } = parseFieldChangeArgs(args);
        if(name !== 'naturalizationCertificate' && name !== 'citizenshipCertificate') {
            const newState = { ...data };
            newState.taxHousehold.householdMember[member] = newData;
            props.actions.changeData(newState);
        }
        setLocalState(state => ({
            ...state,
            ...newData,
        }));
    };

    const onFieldChange_check_if = newData => {

        const {
            lawfulPresenceIndicator,
        } = newData.citizenshipImmigrationStatus;

        newData.citizenshipImmigrationStatus = {
            ...cloneObject(EMPTY_OBJECTS.CITIZENSHIP_STATUS),
            citizenshipStatusIndicator: false,
            lawfulPresenceIndicator
        };

        newData.returnedFromTourOfActiveDuty = null;
        newData.dateLastActiveTour = null;


        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);
    };

    // Updating status of eligibility document

    const onFieldChange_document = newData => {

        // Here comments should be added

        let docForChange = [];
        Object.entries(newData).forEach(doc => {
            const [name, value] = doc;
            const neededDoc = localState.documents.find(doc => doc.documentName === name);
            if (neededDoc) {
                if (neededDoc.documentStatus === value || (neededDoc.documentStatus === true && value === false)) {
                    newData[name] = false;
                } else {
                    docForChange = doc;
                    newData[name] = true;
                }
            }
        });

        setLocalState(state => ({
            ...state,
            documents: state.documents.map(doc => ({
                ...doc,
                documentStatus: docForChange && (doc.documentName === docForChange[0]) ? docForChange[1] : false
            }))
        }));

        const newState = { ...data };
        newState
            .taxHousehold
            .householdMember[member]
            .citizenshipImmigrationStatus
            .citizenshipDocument = cloneObject(EMPTY_OBJECTS.CITIZENSHIP_DOCUMENT);

        newState
            .taxHousehold
            .householdMember[member]
            .citizenshipImmigrationStatus
            .eligibleImmigrationDocumentType[0] = newData;

        props.actions.changeData(newState);
    };

    const checkTopLevelValidation = () => {
        if(naturalizedCitizenshipIndicator) {
            return localState.citizenshipCertificate || localState.naturalizationCertificate;
        } else if(lawfulPresenceIndicator) {
            if(isState.ID || isState.GA) {
                return true;
            }
            return !localState.documents.every(document => document.documentStatus === false);
        } else {
            return true;
        }
    };

    const documentsListClasses = classnames(
        'usa-legend',
        { 'required-true': (!(isState.ID || isState.GA)) }
    );

    return (
        <div id="fhCitizenship" className="fade-in">
            <Form topLevelValidity={checkTopLevelValidation()} {...{ formName, pageName }}>
                <Fragment>
                    <div className="subsection">
                        <UniversalInput
                            legend={{
                                legendText: 'ssap.input.legend.is_citizen',
                                legendTextReplacements: {
                                    personName: (
                                        <strong>
                                            { normalizeNameOfPerson(name) }
                                        </strong>
                                    )
                                }
                            }}
                            label={{
                                showRequiredIcon: true
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'citizenshipStatusIndicator',
                                value: citizenshipStatusIndicator,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: currentData,
                                fields: ['citizenshipImmigrationStatus']

                            }}
                            inputActions={{
                                onFieldChange
                            }}
                            errorClasses={['margin-l-0']}
                        />

                        {
                            citizenshipStatusIndicator === false &&

                                <Fragment>
                                    <span className='grid-row'>
                                        <div className="margin-t-20">
                                            <UniversalInput
                                                label={{
                                                    text: 'ssap.input.label.lawful_presence',
                                                    labelTextReplacements: {
                                                        personName: (
                                                            <strong>
                                                                { normalizeNameOfPerson(name) }
                                                            </strong>
                                                        )
                                                    }
                                                }}
                                                inputData={{
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.CHECKBOX,
                                                    classes:[],
                                                    name: 'lawfulPresenceIndicator',
                                                    checked: lawfulPresenceIndicator || false,
                                                    value: lawfulPresenceIndicator || false,
                                                    currentData: currentData,
                                                    fields: ['citizenshipImmigrationStatus']
                                                }}
                                                inputActions={{
                                                    onFieldChange: onFieldChange_check_if
                                                }}
                                            />
                                        </div>
                                        <InstructionalText
                                            Component = {
                                                <span
                                                    data-instructions = 'instructional_text.immigration_status'
                                                    className = 'required-false margin-t-20 align-self-center'
                                                />
                                            }
                                            skipParsing={true}
                                            replacements = { {
                                                dacaEffective,
                                                exchangeName,
                                                phone: PHONE,
                                                link: (
                                                    <a className="usa-link--external" href={LEARN_MORE_LINK} target="_blank" rel="noopener noreferrer" >
                                                        <FormattedMessage id="instructional_text.immigration_status.link_text" />
                                                    </a>
                                                ),
                                            } }
                                        />
                                    </span>

                                    {/*****************************************
                                     Begin - If #lawfulPresenceIndicator (Yes)
                                     ******************************************/}

                                    {
                                        lawfulPresenceIndicator &&

                                        <Fragment>
                                            <AlertInfo
                                                id='ssap.family_household.cis.document_providing_motivation'
                                                textReplacements={{
                                                    exchangeName,
                                                    helpName: 'Call Center', // hardcoded as of now
                                                    helpNumber: PHONE,
                                                    lineBreak: <br/>
                                                }}
                                            />
                                            <div className="grid-row gi-component gi-radio_buttons">
                                                <fieldset className="usa-fieldset">
                                                    <legend className={documentsListClasses} data-testid="document_selection_legend">
                                                        <FormattedMessage
                                                            id='ssap.family_household.cis.select_document'
                                                            values={{
                                                                personName: normalizeNameOfPerson(name)
                                                            }}
                                                        />
                                                    </legend>
                                                    { (isState.ID || isState.GA) &&
                                                        <div className="usa-alert gi-alert gi-alert__left-accent gi-alert__info">
                                                            <div className="gi-alert__body">
                                                                <p className="gi-alert__text" data-testid="cis_notification_info_msg">
                                                                    <FormattedMessage 
                                                                        id="ssap.family_household.cis.notification_one"
                                                                    />
                                                                </p>
                                                            </div>
                                                        </div>
                                                    }
                                                    <div className="grid-row">
                                                        <ValidationErrorMessage
                                                            isVisible={isFormSubmitted && !checkTopLevelValidation()}
                                                            messageText={VALIDATION_ERROR_MESSAGES.DOCUMENT_TYPE}
                                                            errorClasses={['margin-l-0']}
                                                        />
                                                        <div className="grid-col-12">
                                                            {
                                                                prepareDocumentList().map(statusDocument => {

                                                                    return (
                                                                        <Fragment
                                                                            key={statusDocument.documentName}
                                                                        >
                                                                            <div>
                                                                                <UniversalInput
                                                                                    label={{
                                                                                        text: statusDocument.documentText,
                                                                                        labelClass: ['sr-only', 'usa-legend']
                                                                                    }}
                                                                                    inputData={{
                                                                                        isInputRequired: false,
                                                                                        type: INPUT_TYPES.RADIO_CHECKBOXES,
                                                                                        name: statusDocument.documentName,
                                                                                        checked: documents[statusDocument.documentName] || false,
                                                                                        value: documents[statusDocument.documentName] || false,
                                                                                        currentData: documents,
                                                                                        fields: [],
                                                                                        radioCheckbox: false,
                                                                                        nameRadioCheckbox: 'documentName'
                                                                                    }}
                                                                                    inputActions={{
                                                                                        onFieldChange: onFieldChange_document
                                                                                    }}
                                                                                    indexInArray={member}
                                                                                />

                                                                                {/*****************************************
                                                                                 Begin - If certain document is checked (true)
                                                                                 ******************************************/}

                                                                                {
                                                                                    documents[statusDocument.documentName] ?

                                                                                        <Fragment>
                                                                                            {
                                                                                                statusDocument.documentDetails.map(input => {

                                                                                                    const {
                                                                                                        isInputRequired,
                                                                                                        isToggleReRegistration,
                                                                                                        name,
                                                                                                        options,
                                                                                                        placeholder,
                                                                                                        specificValidation,
                                                                                                        text,
                                                                                                        type,
                                                                                                        validationType,
                                                                                                        validationErrorMessage
                                                                                                    } = input;

                                                                                                    return (
                                                                                                        <UniversalInput
                                                                                                            key={name}
                                                                                                            label={{
                                                                                                                text: text,
                                                                                                                labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label']
                                                                                                            }}
                                                                                                            inputData={{
                                                                                                                divClass: ['tablet:grid-col-6'],
                                                                                                                isInputRequired: isInputRequired,
                                                                                                                placeholder: placeholder,
                                                                                                                type: INPUT_TYPES[type],
                                                                                                                name: name,
                                                                                                                value: currentData.citizenshipImmigrationStatus.citizenshipDocument[name],
                                                                                                                checked: currentData.citizenshipImmigrationStatus.citizenshipDocument[name],
                                                                                                                options: options,
                                                                                                                validationType: validationType,
                                                                                                                validationErrorMessage: validationErrorMessage,
                                                                                                                specificValidation: specificValidation,
                                                                                                                currentData: currentData,
                                                                                                                valuesToMatch: isState,
                                                                                                                fields: ['citizenshipImmigrationStatus', 'citizenshipDocument'],
                                                                                                                isToggleReRegistration: isToggleReRegistration
                                                                                                            }}
                                                                                                            inputActions={{
                                                                                                                onFieldChange
                                                                                                            }}
                                                                                                            indexInArray={member}
                                                                                                        />
                                                                                                    );
                                                                                                })
                                                                                            }
                                                                                        </Fragment> : null
                                                                                }
                                                                            </div>
                                                                        </Fragment>
                                                                    );
                                                                })
                                                            }
                                                        </div>

                                                    </div>
                                                </fieldset>
                                            </div>
                                            <div className="grid-row gi-checkbox">
                                                <fieldset className="usa-fieldset margin-tb-20-10">
                                                    <legend data-testid="additional_documents_legend">
                                                        <span id="additional_documents_legend_text">
                                                            <FormattedMessage
                                                                id="ssap.family_household.cis.additional_documents"
                                                                values={{
                                                                    personName: (
                                                                        <strong>{normalizeNameOfPerson(name)}</strong>
                                                                    ),
                                                                }}
                                                            />
                                                        </span>
                                                        <InstructionalText
                                                            Component={
                                                                <span
                                                                    data-instructions='instructional_text.immigration_additional_documents'
                                                                    className='required-false margin-t-20 align-self-center'
                                                                />
                                                            }
                                                            skipParsing={true}
                                                            replacements={{ exchangeName, phone: PHONE }}
                                                        />
                                                    </legend>
                                                    <div role="group" aria-labelledby="additional_documents_legend_text">
                                                        <OtherDocumentsInputs
                                                            {...{
                                                                selectionMode: isState.MN ? 'radio' : 'checkbox',
                                                                indexInArray: isState.MN ? member : undefined,
                                                                otherImmigrationDocumentType,
                                                                currentData,
                                                                isState,
                                                                inputActions: {
                                                                    onFieldChange
                                                                },
                                                                underFormControl: true
                                                            }}
                                                        />
                                                    </div>
                                                </fieldset>
                                            </div>

                                            {
                                                (isState.MN && otherImmigrationDocumentType.lawfulPermanentResidentIndicator) &&
                                                    <div className="grid-row gi-checkbox">
                                                        <UniversalInput
                                                            legend={{
                                                                legendText: 'ssap.input.legend.mn_has_refugee_status',
                                                                legendTextReplacements: {
                                                                    personName: (
                                                                        <strong>
                                                                            {normalizeNameOfPerson(name)}
                                                                        </strong>
                                                                    )
                                                                }
                                                            }}
                                                            label={{
                                                                showRequiredIcon: true
                                                            }}
                                                            inputData={{
                                                                isInputRequired: true,
                                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                                name: 'hasRefugeeStatus',
                                                                value: hasRefugeeStatus,
                                                                options: DROPDOWN_OPTIONS.YES_NO,
                                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                                currentData: currentData,
                                                                fields: ['citizenshipImmigrationStatus']
                                                            }}
                                                            inputActions={{
                                                                onFieldChange
                                                            }}
                                                            errorClasses={['margin-l-0']}
                                                            indexInArray={member}
                                                        />
                                                    </div>
                                            }

                                            {
                                                shouldRenderSameOnDocument &&

                                                <UniversalInput
                                                    legend={{
                                                        legendText: 'ssap.input.legend.same_name_on_document',
                                                        legendTextReplacements: {
                                                            personName: (
                                                                <strong>
                                                                    { normalizeNameOfPerson(name) }
                                                                </strong>
                                                            ),
                                                            gender
                                                        }
                                                    }}
                                                    label={{
                                                        showRequiredIcon: true
                                                    }}
                                                    inputData={{
                                                        isInputRequired: true,
                                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                                        name: 'nameSameOnDocumentIndicator',
                                                        value: nameSameOnDocumentIndicator,
                                                        options: DROPDOWN_OPTIONS.YES_NO,
                                                        validationType: VALIDATION_TYPES.CHOSEN,
                                                        currentData: currentData,
                                                        fields: ['citizenshipImmigrationStatus', 'citizenshipDocument']
                                                    }}
                                                    errorClasses={['margin-l-0']}
                                                    inputActions={{
                                                        onFieldChange
                                                    }}
                                                />

                                            }

                                            {
                                                nameSameOnDocumentIndicator === false &&

                                                <NamesOnDocumentInputs
                                                    {...{
                                                        firstName,
                                                        middleName,
                                                        lastName,
                                                        suffix,
                                                        currentData: currentData,
                                                        inputActions: {
                                                            onFieldChange
                                                        },
                                                        underFormControl: true,
                                                        stateCode
                                                    }}
                                                />
                                            }

                                            <Fragment>
                                                <UniversalInput
                                                    legend={{
                                                        legendText: 'ssap.input.legend.lives_since',
                                                        legendTextReplacements: {
                                                            personName: (
                                                                <strong>
                                                                    {normalizeNameOfPerson(name)}
                                                                </strong>
                                                            )
                                                        }
                                                    }}
                                                    inputData={{
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                                        name: 'livedIntheUSSince1996Indicator',
                                                        value: livedIntheUSSince1996Indicator,
                                                        options: DROPDOWN_OPTIONS.YES_NO,
                                                        validationType: VALIDATION_TYPES.CHOSEN,
                                                        currentData: currentData,
                                                        fields: ['citizenshipImmigrationStatus']
                                                    }}
                                                    errorClasses={['margin-l-0']}
                                                    inputActions={{
                                                        onFieldChange
                                                    }}
                                                />
                                            </Fragment>

                                            {
                                                livedIntheUSSince1996Indicator === false &&

                                                <Fragment>
                                                    <UniversalInput
                                                        legend={{
                                                            legendText: 'ssap.input.legend.same_status_five_last_years',
                                                            legendTextReplacements: {
                                                                personName: (
                                                                    <strong>
                                                                        {normalizeNameOfPerson(name)}
                                                                    </strong>
                                                                )
                                                            }
                                                        }}
                                                        label={{
                                                            showRequiredIcon: !isState.GA
                                                        }}
                                                        inputData={{
                                                            isInputRequired: !isState.GA,
                                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                                            name: 'unchangedImmigrationStatusLastFiveYears',
                                                            value: unchangedImmigrationStatusLastFiveYears,
                                                            options: DROPDOWN_OPTIONS.YES_NO,
                                                            validationType: VALIDATION_TYPES.CHOSEN,
                                                            currentData: currentData,
                                                            fields: ['citizenshipImmigrationStatus']
                                                        }}
                                                        errorClasses={['margin-l-0']}
                                                        inputActions={{
                                                            onFieldChange
                                                        }}
                                                    />
                                                </Fragment>
                                            }

                                            {
                                                isState.MN &&
                                                <Fragment>
                                                    <UniversalInput
                                                        label={{
                                                            labelClass: ['padding-0'],
                                                            text: 'ssap.input.label.date_Of_Entry'
                                                        }}
                                                        containerClass={['mt-2 pb-3']}
                                                        inputData={{
                                                            isInputRequired: true,
                                                            type: INPUT_TYPES.DATE_PICKER,
                                                            name: 'dateOfEntry',
                                                            value: dateOfEntry,
                                                            placeholder: 'MM/DD/YYY',
                                                            valuesToMatch: [dateOfBirth, serverDate],
                                                            validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                                            validationErrorMessage: isDateAfter(dateOfEntry, serverDate, 'days')
                                                                ? VALIDATION_ERROR_MESSAGES.DATE
                                                                : VALIDATION_ERROR_MESSAGES.DATE_GREATER_THAN_DOB,
                                                            currentData: currentData,
                                                            fields: ['citizenshipImmigrationStatus']
                                                        }}
                                                        inputActions={{
                                                            onFieldChange: onFieldChange
                                                        }}
                                                        errorClasses={['margin-l-0']}
                                                    />
                                                    <UniversalInput
                                                        legend={{
                                                            legendText: 'ssap.input.legend.has_I864_Sponsor',
                                                            legendTextReplacements: {
                                                                personName: (
                                                                    <strong>
                                                                        {normalizeNameOfPerson(name)}
                                                                    </strong>
                                                                )
                                                            }
                                                        }}
                                                        label={{
                                                            showRequiredIcon: true
                                                        }}
                                                        inputData={{
                                                            isInputRequired: true,
                                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                                            name: 'hasI864Sponsor',
                                                            value: hasI864Sponsor,
                                                            options: DROPDOWN_OPTIONS.YES_NO,
                                                            validationType: VALIDATION_TYPES.CHOSEN,
                                                            currentData: currentData,
                                                            fields: ['citizenshipImmigrationStatus']
                                                        }}
                                                        errorClasses={['margin-l-0']}
                                                        inputActions={{
                                                            onFieldChange
                                                        }}
                                                    />
                                                </Fragment>
                                            }
                                        </Fragment>
                                    }

                                    {/*****************************************
                                     End - If #lawfulPresenceIndicator (Yes)
                                     ******************************************/}

                                </Fragment>
                        }


                        {/**********************************************
                         Begin - If #citizenshipStatusIndicator (false)
                         ***********************************************/}

                        {
                            citizenshipStatusIndicator === true &&

                            <Fragment>
                                <UniversalInput
                                    legend={{
                                        legendText: 'ssap.input.legend.is_naturalized',
                                        legendTextReplacements: {
                                            personName: (
                                                <strong>
                                                    { normalizeNameOfPerson(name) }
                                                </strong>
                                            ),
                                            naturalizedCitizenTooltip: (
                                                <Tooltip
                                                    {...{
                                                        id: 'common.naturalized',
                                                        linkDomId: 'common.naturalized',
                                                        content: 'ssap.input.legend.naturalized.tooltip',
                                                        'data-testid': 'ssap_input_legend_is_naturalized'
                                                    }}
                                                />
                                            ),
                                            derivedCitizenTooltip: (
                                                <Tooltip
                                                    {...{
                                                        id: 'common.derived',
                                                        linkDomId: 'common.derived',
                                                        content: 'ssap.input.legend.derived.tooltip',
                                                        'data-testid': 'ssap_input_legend_is_derived'
                                                    }}
                                                />
                                            )
                                        }
                                    }}
                                    label={{
                                        showRequiredIcon: true
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'naturalizedCitizenshipIndicator',
                                        value: naturalizedCitizenshipIndicator,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: currentData,
                                        fields: ['citizenshipImmigrationStatus']

                                    }}
                                    inputActions={{
                                        onFieldChange: onFieldChange_naturalized
                                    }}
                                    errorClasses={['margin-l-0']}
                                />
                        
                                {/***************************************************
                                 Begin - If #naturalizedCitizenshipIndicator (true)
                                 ***************************************************/}
                                {
                                    naturalizedCitizenshipIndicator === true &&
                                        <Fragment>
                                            <ValidationErrorMessage
                                                isVisible={isFormSubmitted && !checkTopLevelValidation()}
                                                messageText={VALIDATION_ERROR_MESSAGES.ANY_CERTIFICATE}
                                                errorClasses={['margin-l-0']}
                                            />
                                            <div className="grid-row gi-component gi-radio_buttons">
                                                <fieldset className="usa-fieldset">
                                                    <legend className = { documentsListClasses } data-testid="document_type_legend" >
                                                        <FormattedMessage id='ssap.input.legend.please_document_type'/>
                                                    </legend>
                                                    <div className="grid-row">
                                                        <ul className="grid-col-12 usa-list usa-list--unstyled">
                                                            <Fragment>
                                                                <li className="">
                                                                    <UniversalInput
                                                                        label={{
                                                                            text: 'ssap.input.label.naturalization_certificate',
                                                                            labelClass: ['sr-only', 'usa-legend']
                                                                        }}
                                                                        inputData={{
                                                                            isInputRequired: true,
                                                                            type: INPUT_TYPES.RADIO_CHECKBOXES,
                                                                            classes:['disp-styl'],
                                                                            name: 'naturalizationCertificate',
                                                                            checked: localState.naturalizationCertificate,
                                                                            value: localState.naturalizationCertificate,
                                                                            currentData: {
                                                                                naturalizationCertificate: localState.naturalizationCertificate,
                                                                                citizenshipCertificate: false
                                                                            },
                                                                            fields: [],
                                                                            radioCheckbox: false,
                                                                            nameRadioCheckbox:'selectDocument'
                                                                        }}
                                                                        inputActions={{
                                                                            onFieldChange: onFieldChange_certificate
                                                                        }}
                                                                    />
                                                                    <ConditionalRendering isTrue={localState.naturalizationCertificate}>
                                                                        <Fragment>
                                                                            <UniversalInput
                                                                                label={{
                                                                                    text: 'ssap.input.label.alien_number',
                                                                                }}
                                                                                inputData={{
                                                                                    isInputRequired: false,
                                                                                    type: INPUT_TYPES.MASKED_INPUT,
                                                                                    helperClasses: [],
                                                                                    name: 'alienNumber',
                                                                                    value: alienNumber,
                                                                                    placeholder: 'ssap.input.placeholder.alien_number',
                                                                                    validationType: VALIDATION_TYPES.ALIEN_NUMBER,
                                                                                    valuesToMatch: isState,
                                                                                    currentData: currentData,
                                                                                    fields: ['citizenshipImmigrationStatus', 'citizenshipDocument']
                                                                                }}
                                                                                inputActions={{
                                                                                    onFieldChange: onFieldChange_certificate
                                                                                }}
                                                                            />
                                                                            <UniversalInput
                                                                                label={{
                                                                                    text: 'ssap.input.label.naturalization_number',
                                                                                }}
                                                                                inputData={{
                                                                                    isInputRequired: false,
                                                                                    type: INPUT_TYPES.MASKED_INPUT,
                                                                                    name: 'sevisId',
                                                                                    helperClasses: [],
                                                                                    value: sevisId,
                                                                                    placeholder: 'ssap.input.placeholder.naturalization_number',
                                                                                    validationType: VALIDATION_TYPES.CERTIFICATE_NUMBER,
                                                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.NATURALIZATION_NUMBER,
                                                                                    currentData: currentData,
                                                                                    fields: ['citizenshipImmigrationStatus', 'citizenshipDocument']
                                                                                }}
                                                                                inputActions={{
                                                                                    onFieldChange: onFieldChange_certificate
                                                                                }}
                                                                            />
                                                                        </Fragment>
                                                                    </ConditionalRendering>
                                                                </li>
                                                                <li className="">
                                                                    <UniversalInput
                                                                        label={{
                                                                            text: 'ssap.input.label.certificate_of_citizenship',
                                                                            labelClass: ['sr-only', 'usa-legend']
                                                                        }}
                                                                        inputData={{
                                                                            isInputRequired: false,
                                                                            classes:['disp-styl'],
                                                                            type: INPUT_TYPES.RADIO_CHECKBOXES,
                                                                            name: 'citizenshipCertificate',
                                                                            checked: localState.citizenshipCertificate,
                                                                            value: localState.citizenshipCertificate,
                                                                            currentData: {
                                                                                citizenshipCertificate: localState.citizenshipCertificate,
                                                                                naturalizationCertificate: false
                                                                            },
                                                                            fields: [],
                                                                            radioCheckbox: false,
                                                                            nameRadioCheckbox:'selectDocument'
                                                                        }}
                                                                        inputActions={{
                                                                            onFieldChange: onFieldChange_certificate
                                                                        }}
                                                                    />

                                                                    {/************************************************
                                                                     Begin - If #citizenshipAsAttestedIndicator (Yes)
                                                                    *************************************************/}
                                                                    <ConditionalRendering isTrue={localState.citizenshipCertificate}>
                                                                        <Fragment>
                                                                            <UniversalInput
                                                                                label={{
                                                                                    text: 'ssap.input.label.alien_number',
                                                                                }}
                                                                                inputData={{
                                                                                    isInputRequired: false,
                                                                                    type: INPUT_TYPES.MASKED_INPUT,
                                                                                    name: 'alienNumber',
                                                                                    helperClasses: [],
                                                                                    value: alienNumber,
                                                                                    placeholder: 'ssap.input.placeholder.alien_number',
                                                                                    validationType: VALIDATION_TYPES.ALIEN_NUMBER,
                                                                                    valuesToMatch: isState,
                                                                                    currentData: currentData,
                                                                                    fields: ['citizenshipImmigrationStatus', 'citizenshipDocument']
                                                                                }}
                                                                                inputActions={{
                                                                                    onFieldChange: onFieldChange_certificate
                                                                                }}
                                                                            />
                                                                            <UniversalInput
                                                                                label={{
                                                                                    text: 'ssap.input.label.citizenship_certificate_number',
                                                                                }}
                                                                                inputData={{
                                                                                    isInputRequired: false,
                                                                                    type: INPUT_TYPES.MASKED_INPUT,
                                                                                    name: 'sevisId',
                                                                                    helperClasses: [],
                                                                                    value: sevisId,
                                                                                    placeholder: 'ssap.input.placeholder.citizenship_certificate_number',
                                                                                    validationType: VALIDATION_TYPES.CERTIFICATE_NUMBER,
                                                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.CERTIFICATE_NUMBER,
                                                                                    currentData: currentData,
                                                                                    fields: ['citizenshipImmigrationStatus', 'citizenshipDocument']
                                                                                }}
                                                                                inputActions={{
                                                                                    onFieldChange: onFieldChange_certificate
                                                                                }}
                                                                            />
                                                                        </Fragment>
                                                                    </ConditionalRendering>
                                                                </li>

                                                            </Fragment>
                                                        </ul>
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </Fragment>
                                }
                            </Fragment>
                        }

                        {/**********************************************
                         End - If #citizenshipStatusIndicator (false)
                         ***********************************************/}

                    </div>
                </Fragment>
            </Form>
        </div>
    );
};

const mapStateToProps = state => ({
    common: state.common,
    countries: state.ssap.countries,
    data: state.ssap.data,
    form: state.ssap.form
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearFormSubmission,
        fetchCountries
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_09);
